// Loading, failed, empty — the three non-happy outcomes every read has.

import type { ReactNode } from "react";
import styles from "./Feedback.module.css";
import { Button } from "./Button.js";
import { describeError } from "../api/problem.js";

export interface SpinnerProps {
  /** Rendered, not just announced: "Loading …" is the whole message. */
  label: string;
  inline?: boolean;
}

export function Spinner({ label, inline = false }: SpinnerProps): ReactNode {
  return (
    <div
      className={[styles.loading, inline && styles.inline].filter(Boolean).join(" ")}
      role="status"
    >
      <span className={styles.spinner} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export interface ProblemMessageProps {
  error: unknown;
  /** What the app was doing, e.g. "load the deals list". */
  action: string;
  onRetry?: () => void;
}

/**
 * Shows the problem's `title` and `detail` — the prose the API wrote for a
 * human — plus the stable `code`, so a reviewer reporting a bug can quote the
 * one string that is safe to branch on (§06).
 */
export function ProblemMessage({ error, action, onRetry }: ProblemMessageProps): ReactNode {
  const described = describeError(error);

  return (
    <div className={styles.problem} role="alert">
      <div className={styles.problemBody}>
        <div className={styles.problemTitle}>Could not {action}</div>
        {described.title}
        {described.detail !== undefined && (
          <span className={styles.problemDetail}>{described.detail}</span>
        )}
        {described.code !== undefined && (
          <span className={`${styles.problemCode} mono`}>
            {described.code}
            {described.status !== undefined && ` · HTTP ${described.status}`}
          </span>
        )}
      </div>
      {onRetry !== undefined && (
        <div className={styles.problemActions}>
          <Button small onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

export interface NoticeProps {
  tone: "warn" | "info";
  /** The situation, in one sentence. Rendered in the tone's colour. */
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}

/**
 * A boxed message that is NOT a failure — a 409 waiting on a decision, a
 * warning about what a delete takes with it. `role="status"` rather than
 * `alert`: it accompanies something the user is already looking at.
 */
export function Notice({ tone, title, children, actions }: NoticeProps): ReactNode {
  const toneClass = tone === "warn" ? styles.noticeWarn : styles.noticeInfo;
  return (
    <div className={`${styles.notice} ${toneClass}`} role="status">
      <div className={styles.noticeBody}>
        <div className={styles.noticeTitle}>{title}</div>
        {children}
      </div>
      {actions !== undefined && <div className={styles.noticeActions}>{actions}</div>}
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  /**
   * Say why it is empty and what would fill it. "No results" tells a reader
   * nothing they didn't already know from looking at the blank panel.
   */
  description: string;
}

export function EmptyState({ title, description }: EmptyStateProps): ReactNode {
  return (
    <div className={styles.empty}>
      <div className={styles.emptyTitle}>{title}</div>
      <div
        className={styles.emptyBody}
        {...(typeof description === "string" ? { title: description } : {})}
      >
        {description}
      </div>
    </div>
  );
}
